/**
 * @file StatementStepSend.js
 * @version 2.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-20
 * @modified 2026-10-20
 *
 * Generate Statements, step 4 — Send: only WHERE the statements go, a plain
 * summary of what will go, and the checks the send waits for as a list with a
 * state each (rather than a refusal after the button is pressed).
 *
 * "By contact group" (Cam, 20 Oct 2026): Email Invoices members are emailed
 * (email layout), Postal Invoices members printed (print layout), members of
 * both get both; anyone in neither is chosen here. One send — the emails go
 * first, then the print, each with its own progress.
 *
 * 2.0.0 (2026-10-20, Cam — the statements look): destinations as option
 * cards ("By contact group" featured); each customer in neither group chosen
 * with a segmented Email / Print / Both / Leave out; the summary as stat
 * tiles; "Before it can go" as a checklist with a solid icon per state, the
 * Accounts confirmation inside the item it unblocks.
 */

import React, { useMemo } from 'react';
import Input from './Input';
import { routingPlan, routeFor } from '../utils/statementRunKind';
import { accountLabel } from '../utils/statementOrder';

export const DESTINATIONS = [
  { key: 'groups', icon: 'alt_route', title: 'By contact group', text: 'Email Invoices emailed, Postal Invoices printed' },
  { key: 'customers', icon: 'forward_to_inbox', title: 'Email every customer', text: 'Email layout, to their statement addresses' },
  { key: 'print', icon: 'print', title: 'Print every statement', text: 'Print layout, statement and invoice trays' },
  { key: 'office', icon: 'mail', title: 'Email to me / the office', text: 'One email per statement, to one address' },
  { key: 'save', icon: 'folder_zip', title: 'Download a ZIP', text: 'One PDF per statement' }
];

const CHOICES = [
  { value: 'email', label: 'Email' },
  { value: 'print', label: 'Print' },
  { value: 'both', label: 'Both' },
  { value: 'skip', label: 'Leave out' }
];

const GATE_ICON = { ok: 'check', need: 'priority_high', info: 'info', wait: 'hourglass_top' };

/**
 * @param {object} props
 * @param {string} props.destination
 * @param {function} props.onDestination
 * @param {Array} props.statements
 * @param {object} props.routeChoices - { [statementId]: 'email'|'print'|'both'|'skip' }
 * @param {function} props.onRouteChoice - (id, choice)
 * @param {object} props.content
 * @param {Set} props.includeInvoiceIds
 * @param {string} props.templateMode
 * @param {function} props.onTemplateMode
 * @param {string} props.officeEmail
 * @param {function} props.onOfficeEmail
 * @param {Array<{ key?, state, text, icon? }>} props.gates
 * @param {React.ReactNode} props.progress - loading bar / results
 * @param {React.ReactNode} props.accounts - the Accounts confirmation, shown inside the gate keyed 'accounts'
 */
function StatementStepSend({
  destination, onDestination, statements, routeChoices, onRouteChoice, content, includeInvoiceIds,
  templateMode, onTemplateMode, officeEmail, onOfficeEmail, gates, progress, accounts
}) {
  const approved = statements.filter(s => s.approval_status === 'approved');
  const plan = useMemo(() => routingPlan(statements, routeChoices), [statements, routeChoices]);
  const withInvoices = approved.filter(s => includeInvoiceIds.has(s.id));
  const invoiceTotal = withInvoices.reduce((n, s) => n + (s.invoiceCount || 0), 0);
  const noAddress = approved.filter(s => !String(s.email || '').trim());
  const neitherRows = approved.filter(s => routeFor(s).needsChoice);
  const unchosen = neitherRows.filter(s => !routeChoices[s.id]).length;
  const pl = (n, one, many) => `${n} ${n === 1 ? one : many}`;

  const contentWords = [
    content.include === 'statement' ? 'Statement only' : content.include === 'invoices' ? 'Invoices only' : 'Statement and invoices',
    content.includeAllUnpaidInvoices ? 'unpaid invoices listed' : 'aged summary',
    content.listUsedCredits || content.attachUsedCredits
      ? `fully-used credit notes ${[content.listUsedCredits && 'listed', content.attachUsedCredits && 'attached'].filter(Boolean).join(' and ')}`
      : 'fully-used credit notes left off'
  ].join(' · ');

  const stats = [{ n: approved.length, text: `approved of ${statements.length}` }];
  if (destination === 'groups') {
    stats.push({ n: plan.emailIds.length, text: 'emailed' });
    stats.push({ n: plan.printIds.length, text: plan.both ? `printed · ${plan.both} both` : 'printed' });
    stats.push(unchosen ? { n: unchosen, text: 'to choose', need: true } : { n: plan.skipped.length, text: 'left out' });
  } else if (destination === 'customers') {
    stats.push({ n: approved.length - noAddress.length, text: 'emailed' });
    stats.push({ n: noAddress.length, text: 'no email address', need: noAddress.length > 0 });
    stats.push({ n: withInvoices.length, text: 'with invoices' });
  } else if (destination === 'print') {
    stats.push({ n: approved.length, text: 'printed' });
    stats.push({ n: withInvoices.length, text: 'with invoices' });
    stats.push({ n: invoiceTotal, text: 'invoices in all' });
  } else {
    stats.push({ n: withInvoices.length, text: 'with invoices' });
    stats.push({ n: invoiceTotal, text: 'invoices in all' });
  }

  return (
    <div className="sr-send">
      <section className="sr-panel sr-send-where">
        <div className="sr-card-head">
          <div>
            <h4 className="sr-card-title">Where they go</h4>
            <p className="sr-card-sub">Only approved statements are sent</p>
          </div>
        </div>
        <div className="sr-card-body">
          <div className="sr-dests" role="radiogroup" aria-label="Where they go">
            {DESTINATIONS.map(d => (
              <button key={d.key} type="button" role="radio" aria-checked={destination === d.key}
                className={`sr-option${destination === d.key ? ' on' : ''}${d.key === 'groups' ? ' featured' : ''}`} onClick={() => onDestination(d.key)}>
                <span className="sr-radio" aria-hidden="true" />
                <span>
                  <span className="sr-option-title"><span className="material-symbols-rounded">{d.icon}</span>{d.title}</span>
                  <span className="sr-option-text">{d.text}</span>
                </span>
              </button>
            ))}
          </div>
          {(destination === 'office' || destination === 'save') && (
            <div className="sr-dest-options">
              {destination === 'office' && (
                <label className="sr-inline-field">
                  <span>Send to</span>
                  <Input type="email" value={officeEmail} onChange={(e) => onOfficeEmail(e.target.value)} />
                </label>
              )}
              <div className="sr-seg" role="radiogroup" aria-label="Layout">
                {['email', 'print'].map(t => (
                  <button key={t} type="button" role="radio" aria-checked={templateMode === t}
                    className={templateMode === t ? 'on' : ''} onClick={() => onTemplateMode(t)}>
                    <span className="material-symbols-rounded">{t === 'email' ? 'mail' : 'print'}</span>
                    {t === 'email' ? 'Email layout' : 'Print layout'}
                  </button>
                ))}
              </div>
            </div>
          )}

          {destination === 'groups' && neitherRows.length > 0 && (
            <div className="sr-section split">
              <span className="sr-label">In neither group <span className={`sr-count${unchosen ? ' error' : ''}`}>{neitherRows.length}</span></span>
              <p className="sr-help">Choose for each — the send waits until every one is chosen.</p>
              {neitherRows.map(s => {
                const name = accountLabel(s.accountNo, s.account);
                return (
                  <div key={s.id} className={`sr-route-row${routeChoices[s.id] ? '' : ' need'}`}>
                    <span className="sr-name">{name}{!String(s.email || '').trim() ? <span className="sr-muted"> · no email address</span> : null}</span>
                    <div className="sr-seg" role="radiogroup" aria-label={`Where ${name} goes`}>
                      {CHOICES.map(c => (
                        <button key={c.value} type="button" role="radio" aria-checked={routeChoices[s.id] === c.value}
                          className={routeChoices[s.id] === c.value ? 'on' : ''} onClick={() => onRouteChoice(s.id, c.value)}>
                          {c.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {progress}
        </div>
      </section>

      <section className="sr-panel sr-send-summary">
        <div className="sr-card-head">
          <div>
            <h4 className="sr-card-title">What will go</h4>
            <p className="sr-card-sub">{contentWords}</p>
          </div>
        </div>
        <div className="sr-card-body">
          <div className="sr-stats">
            {stats.map((t, i) => (
              <div key={i} className={`sr-stat${t.need ? ' need' : ''}`}><b>{t.n}</b><span>{t.text}</span></div>
            ))}
          </div>
          <ul className="sr-summary-notes">
            {statements.length - approved.length > 0 && (
              <li><span className="material-symbols-rounded">info</span>{pl(statements.length - approved.length, 'statement is', 'statements are')} unapproved and not sent.</li>
            )}
            {destination === 'groups' && plan.noAddress.length > 0 && (
              <li><span className="material-symbols-rounded">info</span>{pl(plan.noAddress.length, 'customer in Email Invoices has', 'customers in Email Invoices have')} no email address &mdash; printed instead.</li>
            )}
            {destination === 'groups' && plan.skipped.length > 0 && (
              <li><span className="material-symbols-rounded">info</span>{pl(plan.skipped.length, 'left out', 'left out')} on this send.</li>
            )}
            {destination === 'customers' && noAddress.length > 0 && (
              <li className="bad"><span className="material-symbols-rounded">error</span>{pl(noAddress.length, 'customer has', 'customers have')} no email address: {noAddress.map(s => accountLabel(s.accountNo, s.account)).join(', ')}</li>
            )}
            <li><span className="material-symbols-rounded">receipt_long</span>{withInvoices.length} include invoices ({invoiceTotal} in all)</li>
          </ul>

          <div className="sr-section split">
            <span className="sr-label">Before it can go</span>
            <ul className="sr-checklist">
              {gates.map((g, i) => (
                <li key={g.key || i} className={`sr-gate ${g.state}`}>
                  <span className={`sr-dot ${g.state}`} aria-hidden="true">
                    <span className="material-symbols-rounded">{g.icon || GATE_ICON[g.state] || 'info'}</span>
                  </span>
                  <div className="sr-gate-body">
                    {g.key === 'accounts' && accounts ? accounts : <span>{g.text}</span>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}

export default StatementStepSend;
